import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Truck, 
  Shield, 
  Clock, 
  Phone, 
  Mail, 
  ArrowRight, 
  CheckCircle2, 
  Zap, 
  Home as HomeIcon, 
  Globe, 
  Package,
  Users,
  Car,
  Lock,
  Boxes,
  Weight
} from 'lucide-react';
import { useMetadata } from '../hooks/useMetadata';
import { servicesData, advantagesData, serviceAreas } from '../data';

export default function Home() {
  const [cmsPage, setCmsPage] = useState(null);
  const [activeService, setActiveService] = useState('general');

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/forside')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente forside fra CMS');
        return res.json();
      })
      .then((data) => {
        if (data && data.collectionName === 'pages') {
          setCmsPage(data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  // Brug metadata fra CMS eller fallback
  const title = cmsPage?.meta_title || 'FLOW Transport & Logistik ApS';
  const description =
    cmsPage?.meta_description ||
    'FLOW Transport & Logistik ApS tilbyder professionel transport og logistik med fokus på fleksibilitet, pålidelighed og effektiv levering fra Vanløse og omegn.';

  useMetadata(title, description);

  // Detaljeret indhold til det interaktive center-panel i "Vores Services"
  const interactiveServices = {
    general: {
      title: 'FLOW Transport & Logistik',
      subtitle: 'Fra flyttekasse til fuld logistik',
      description: 'Vi tilbyder transportløsninger i hele Danmark med fokus på pålidelighed, fleksibilitet og personlig service. Klik på ydelserne til højre for at læse mere om vores specifikke løsninger.',
      bullet: 'Fra kurérkørsel og flytning til komplette logistikopgaver',
      link: '/ydelser'
    },
    kurer: {
      title: 'Kurérkørsel',
      subtitle: 'Hurtig dør-til-dør levering',
      description: 'Vi tilbyder lynhurtig kurérkørsel i hele landet. Perfekt til vigtige dokumenter, pakker eller hasteopgaver, der kræver øjeblikkelig afhentning og direkte levering.',
      bullet: 'Direkte levering uden omlastning og unødige stop',
      link: '/ydelser'
    },
    ekspres: {
      title: 'Ekspres transport',
      subtitle: 'Når hvert minut tæller',
      description: 'Vi tilbyder skræddersyede ekspresløsninger til virksomheder, der har behov for hurtig og pålidelig transport. Med fokus på effektiv koordinering og sikker håndtering sikrer vi, at dine varer når frem hurtigt og under de bedst mulige forhold. Vi arbejder målrettet for at levere en service, hvor fleksibilitet, punktlighed og høj kvalitet går hånd i hånd – så du altid kan have tillid til, at din levering er i trygge hænder.',
      bullet: 'Landsdækkende dag-til-dag levering med fuld sporbarhed',
      link: '/ydelser'
    },
    flytning: {
      title: 'Flytning',
      subtitle: 'Sikker og tryg flytteservice',
      description: 'Vi hjælper både private og erhverv med professionel flytning. Vi håndterer dine ejendele med største omhu, fra pakning og transport til udpakning og montering.',
      bullet: 'Erfarne og forsigtige flyttefolk med det rette udstyr',
      link: '/ydelser'
    },
    gods: {
      title: 'Godstransport',
      subtitle: 'Sikker fragt af større partier',
      description: 'Professionel godstransport af paller, stykgods og større partier. Vi råder over moderne materiel og sikrer stabil, forsikret og sikker transport af alle typer gods.',
      bullet: 'Sikker fastspænding, godkendt materiel og fuld forsikring',
      link: '/ydelser'
    },
    tungloeft: {
      title: 'Tungløft & Specialtransport',
      subtitle: 'Håndtering af det ekstraordinære',
      description: 'Har du gods, der kræver særlig ekspertise, kran eller ekstra tungt løfteudstyr? Vi planlægger og udfører specialtransport og tunge løft med højeste sikkerhed.',
      bullet: 'Erfarne specialistas og godkendt udstyr til komplekse opgaver',
      link: '/ydelser'
    },
    logistik: {
      title: 'Logistikløsninger',
      subtitle: 'Effektiv koordinering og omlastning',
      description: 'Vi tilbyder skræddersyede logistikløsninger med fokus på effektiv transport, ruteoptimering og leveringssikkerhed. Gennem tæt dialog og fleksibel planlægning hjælper vi vores kunder med at optimere deres forsyningskæde og sikre pålivelige leverancer.',
      bullet: 'Skræddersyet logistikstyring og effektiv omlastning',
      link: '/ydelser'
    }
  };

  const currentDetails = interactiveServices[activeService] || interactiveServices.general;

  return (
    <div className="w-full font-sans">
      {/* Hero Banner Section */}
      <section 
        className="relative w-full min-h-[380px] sm:min-h-[520px] bg-cover bg-center flex items-center [background-image:var(--hero-bg-mobile)] sm:[background-image:var(--hero-bg-desktop)]" 
        style={{ 
          '--hero-bg-mobile': `url('https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/ecc7c3d2-b2a3-4a1d-ba29-5687874f0c20/chatgpt-image-25-jun-2026-13-22-05.png')`,
          '--hero-bg-desktop': `url('https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/ecc7c3d2-b2a3-4a1d-ba29-5687874f0c20/chatgpt-image-17-jun-2026-15-31-13.png')`
        }}
      >
        {/* Tint overlay for text legibility */}
        <div className="absolute inset-0 bg-black/45 sm:bg-black/40"></div>
        
        <div className="relative z-10 max-w-7xl lg:mx-0 lg:mr-auto px-6 md:px-12 lg:pl-16 xl:pl-24 w-full text-white py-12 sm:py-20 flex flex-col items-center sm:items-start text-center sm:text-left">
          <h1 className="text-7xl sm:text-8xl font-display italic font-black tracking-tight drop-shadow-md leading-none">
            FLOW
          </h1>
          <p className="text-2xl sm:text-3xl font-sans font-bold mt-1 tracking-wide uppercase drop-shadow-sm">
            Transport & Logistik
          </p>
          <p className="text-lg sm:text-xl font-sans font-medium mt-6 max-w-md drop-shadow-sm text-slate-100 mx-auto sm:mx-0">
            Fra flyttekasse til fuld logistik
          </p>
          
          {/* Hero Call to Action */}
          <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-8">
            <Link 
              to="/kontakt" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary hover:bg-brand-primary/95 text-white font-sans text-sm font-semibold rounded shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              Kontakt
              <span className="text-xs">&rsaquo;</span>
            </Link>
            <Link 
              to="/ydelser" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-sans text-sm font-semibold rounded shadow-md transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              Vores ydelser
            </Link>
          </div>
        </div>
      </section>

      {/* The "Vores Services" Grid (Interactive Components) */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-sm font-bold text-brand-primary uppercase tracking-wider mb-2">
              Vores services
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-primary tracking-tight">
              Fleksible transportløsninger i hele Danmark
            </h2>
            <p className="mt-4 text-slate-600 text-sm sm:text-base">
              Vi dækker alle dine behov inden for transport og logistik. Klik på knapperne til højre for at udforske vores services og se vores dækningsområde på kortet.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Column 1: Content Copy */}
            <div className="space-y-5 bg-slate-50/50 p-8 rounded-2xl border border-slate-100 min-h-[320px] flex flex-col justify-between">
              <div className="space-y-4">
                <span className="text-xs font-bold text-brand-primary uppercase tracking-wider bg-brand-primary/10 px-2.5 py-1 rounded-full">
                  {currentDetails.subtitle}
                </span>
                <h3 className="text-2xl font-serif font-bold text-brand-primary pt-1">
                  {currentDetails.title}
                </h3>
                <div className="text-sm text-brand-dark/90 space-y-4 leading-relaxed font-sans">
                  <p>{currentDetails.description}</p>
                  <p className="border-l-2 border-brand-primary pl-3 italic text-brand-muted text-xs sm:text-sm">
                    {currentDetails.bullet}
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Link 
                  to={currentDetails.link} 
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:underline cursor-pointer"
                >
                  Læs mere om vores ydelser
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* Column 2: 2-Column Action Cards */}
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setActiveService(activeService === 'kurer' ? 'general' : 'kurer')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'kurer' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <Car className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Kurérkørsel</span>
              </button>
              
              <button 
                onClick={() => setActiveService(activeService === 'ekspres' ? 'general' : 'ekspres')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'ekspres' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <Globe className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Ekspres transport</span>
              </button>
              
              <button 
                onClick={() => setActiveService(activeService === 'flytning' ? 'general' : 'flytning')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'flytning' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <HomeIcon className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Flytning</span>
              </button>

              <button 
                onClick={() => setActiveService(activeService === 'gods' ? 'general' : 'gods')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'gods' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <Lock className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Godstransport</span>
              </button>

              <button 
                onClick={() => setActiveService(activeService === 'tungloeft' ? 'general' : 'tungloeft')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'tungloeft' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <Weight className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Tungløft</span>
              </button>

              <button 
                onClick={() => setActiveService(activeService === 'logistik' ? 'general' : 'logistik')}
                className={`flex flex-col items-center justify-center p-6 rounded-2xl shadow-3d-card transition-all cursor-pointer border ${
                  activeService === 'logistik' 
                    ? 'bg-brand-primary text-white border-brand-primary scale-[1.02]' 
                    : 'bg-surface-card text-brand-dark border-white/40 hover:scale-[1.02] hover:bg-white'
                }`}
              >
                <Boxes className="h-8 w-8 mb-2 text-current shrink-0" />
                <span className="text-xs font-bold text-center tracking-tight leading-tight">Logistik</span>
              </button>
            </div>

          </div>
        </div>
      </section>

      {/* Præsentation af virksomheden */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Venstre: Tekst */}
            <div className="space-y-6">
              <div className="text-sm font-bold text-brand-primary uppercase tracking-wider">
                Hvem er vi
              </div>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-primary tracking-tight">
                Driftssikker og fleksibel transport i højsædet
              </h2>
              <div 
                className="text-slate-600 leading-relaxed space-y-4 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: cmsPage?.content || '<p>Vi er et team af erfarne danske chauffører med mange års erfaring inden for transport- og flyttebranchen. Gennem årene har vi hjulpet både private og virksomheder med sikre, effektive og fleksible transportløsninger, hvor kvalitet og service altid er i fokus.</p><p>For os handler transport om mere end blot at flytte gods fra A til B. Det handler om at skabe tryghed, overholde aftaler og levere en service, som kunderne kan stole på. Derfor møder vi hver opgave med professionalisme, ansvarlighed og respekt for både kundens tid og værdier.</p><p>Vores erfaring gør os i stand til at håndtere alt fra mindre transportopgaver til større og mere komplekse flytninger. Uanset opgavens omfang arbejder vi med samme mål for øje – at levere en løsning, der er effektiv, sikker og tilpasset kundens behov.</p><p>Når du vælger os, vælger du en samarbejdspartner, der sætter pålidelighed, fleksibilitet og kundetilfredshed i første række.</p>' }}
              />
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-2.5 text-brand-dark font-semibold text-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0" />
                  <span>100% Dansk CVR & forsikring</span>
                </div>
                <div className="flex items-center gap-2.5 text-brand-dark font-semibold text-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0" />
                  <span>Personlig kundeservice</span>
                </div>
                <div className="flex items-center gap-2.5 text-brand-dark font-semibold text-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0" />
                  <span>Erfarne og høflige chauffører</span>
                </div>
                <div className="flex items-center gap-2.5 text-brand-dark font-semibold text-sm">
                  <CheckCircle2 className="h-5 w-5 text-brand-primary shrink-0" />
                  <span>Skræddersyede logistikløsninger</span>
                </div>
              </div>
            </div>

            {/* Højre: Billede */}
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-primary/10 rounded-2xl transform rotate-2" />
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,height=600,fit=cover,quality=85,format=auto/assets/ecc7c3d2-b2a3-4a1d-ba29-5687874f0c20/chatgpt-image-17-jun-2026-15-36-18.png"
                alt="FLOW Transport professionel chauffør"
                className="relative z-10 rounded-xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Hvorfor vælge FLOW Transport & Logistik ApS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-sm font-bold text-brand-primary uppercase tracking-wider mb-2">
              Dine fordele
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-primary tracking-tight">
              Hvorfor vælge FLOW Transport & Logistik ApS?
            </h2>
            <p className="mt-4 text-slate-600 text-sm sm:text-base">
              Vi bygger vores forretning på tillid, kvalitet og tæt dialog med vores kunder. Hvad kan du forvente af os:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantagesData.map((adv, idx) => {
              const IconComponent = adv.icon;
              return (
                <div key={idx} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 space-y-4 text-center shadow-sm">
                  <div className="inline-flex p-4 rounded-full bg-brand-primary/10 text-brand-primary">
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-dark">
                    {adv.title}
                  </h3>
                  <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                    {adv.description}
                  </p>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Kontakt-CTA & Kontaktoplysninger */}
      <section className="bg-brand-dark text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#0056b3_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Venstre: CTA */}
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight">
                Klar til at optimere din transport og logistik?
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Uanset om du har brug for en enkelt kurerkørsel eller en fast distributionsaftale, står vi klar til at hjælpe dig. Kontakt os i dag for et uforpligtende tilbud tilpasset dine behov.
              </p>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center bg-brand-primary hover:bg-brand-primary/95 text-white px-8 py-4 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors shadow-lg cursor-pointer"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>

            {/* Højre: Kontaktoplysninger */}
            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-2xl space-y-6">
              <h3 className="text-xl font-bold text-white border-b border-slate-800 pb-4">
                Direkte kontakt
              </h3>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider">Ring til os</div>
                    <a href="tel:+4522289310" className="text-lg font-bold hover:text-brand-primary transition-colors whitespace-nowrap">
                      22 28 93 10
                    </a>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                  <div className="p-3 rounded-lg bg-brand-primary/10 text-brand-primary shrink-0">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider">Send en mail</div>
                    <a href="mailto:kontakt@flowtl.dk" className="text-lg font-bold hover:text-brand-primary transition-colors break-all sm:whitespace-nowrap">
                      kontakt@flowtl.dk
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
